"use client";

import { type CSSProperties, useEffect, useRef, useState } from "react";

/**
 * Animated height lock for step-based wizards.
 *
 * Each step is a separate subtree with a different content height. Without
 * this, the card (and everything below it) reflows on every step change - * worst on the tall contact step. The hook measures the current step's
 * natural height via a ResizeObserver and exposes it as an exact `height` on
 * the shell. {@link WizardStepShell} then animates `height` (with overflow
 * clipped during the transition), so the card resizes smoothly up AND down on
 * each change instead of jumping.
 *
 * `floor` sets a lower bound (px) - the card never gets shorter than this.
 * Used on hero variants so the first step doesn't look cramped. 0 (default) =
 * pure content-driven height.
 *
 * Measurement happens on the inner `stepsRef` div (unaffected by the lock),
 * so the outer div's exact height doesn't feed back into the measurement.
 * `height` is `null` until the first measurement, so the shell renders at its
 * natural height on mount (no 0→h jump).
 */
export function useWizardHeightLock({ floor = 0 }: { floor?: number } = {}) {
  const rootRef = useRef<HTMLDivElement>(null);
  const stepsRef = useRef<HTMLDivElement>(null);
  const [contentHeight, setContentHeight] = useState<number | null>(null);

  useEffect(() => {
    const el = stepsRef.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      const h = entries[0]?.contentRect.height;
      if (h != null) setContentHeight(h);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  function scrollIntoView() {
    const el = rootRef.current;
    if (!el) return;
    const top = el.getBoundingClientRect().top;
    if (top < 0) {
      window.scrollTo({ top: window.scrollY + top - 16, behavior: "instant" });
    }
  }

  const target = contentHeight == null ? null : Math.max(contentHeight, floor);
  const lockStyle: CSSProperties | undefined = target ? { height: `${target}px` } : undefined;

  return { rootRef, stepsRef, lockStyle, scrollIntoView };
}
